import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "../api/client";

const API_BASE = process.env.REACT_APP_UPLOAD_URL || "";

const resolveAssetUrl = (url?: string) => {
  if (!url) return "";
  if (url.startsWith("http://") || url.startsWith("https://")) return url;
  if (url.startsWith("/")) return `${API_BASE}${url}`;
  return `${API_BASE}/${url}`;
};

type Course = {
  id: string;
  title: string;
  slug: string;
  description: string;
  thumbnail: string;
  price?: number;
  published: boolean;
};

export function Courses() {
  const [courses, setCourses] = useState<Course[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    api
      .get("/courses")
      .then((res) => {
        if (res.data.success) setCourses(res.data.data || []);
      })
      .catch(() => setError("Could not load courses."))
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <div className="min-h-full bg-stone/40">
        <div className="bg-ink-950 text-white py-12">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="animate-pulse h-8 w-48 bg-ink-800 rounded" />
            <div className="mt-2 h-6 w-72 bg-ink-800 rounded" />
          </div>
        </div>
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[1, 2, 3, 4, 5, 6].map((i) => (
              <div key={i} className="animate-pulse rounded-2xl border border-gray-200 bg-white overflow-hidden">
                <div className="aspect-[16/10] bg-gray-100" />
                <div className="p-6 space-y-3">
                  <div className="h-5 w-3/4 bg-gray-200 rounded" />
                  <div className="h-4 w-full bg-gray-100 rounded" />
                  <div className="h-4 w-2/3 bg-gray-100 rounded" />
                  <div className="h-6 w-20 bg-gray-200 rounded mt-4" />
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }
  if (error) {
    return (
      <div className="min-h-full bg-stone/40 flex items-center justify-center px-4 py-16">
        <div className="rounded-2xl border border-red-200 bg-white p-10 text-center shadow-soft max-w-md">
          <div className="w-14 h-14 mx-auto rounded-full bg-red-100 flex items-center justify-center text-red-500">
            <svg className="w-7 h-7" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
          </div>
          <p className="text-red-600 font-medium mt-4">{error}</p>
          <Link to="/" className="mt-6 inline-block font-semibold text-gold-500 hover:text-gold-400 transition-colors">
            Back to home
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-full bg-stone/40">
      <div className="bg-ink-950 text-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-10 md:py-12">
          <p className="text-gold-300/90 font-semibold text-xs tracking-[0.2em] uppercase">
            Catalog
          </p>
          <h1 className="font-serif text-3xl md:text-4xl font-semibold tracking-tight mt-2">
            Course catalog
          </h1>
          <p className="mt-2 text-slate-400 text-sm max-w-xl">
            High-quality continuing education in Health &amp; Wellness and ABA. Enroll to learn at your pace and earn certificates.
          </p>
          {courses.length > 0 && (
            <p className="mt-3 text-slate-500 text-sm">
              {courses.length} course{courses.length !== 1 ? "s" : ""} available
            </p>
          )}
        </div>
        <div className="h-px w-full bg-gradient-to-r from-transparent via-gold-400/30 to-transparent" aria-hidden />
      </div>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-10 md:py-12">
        {courses.length === 0 ? (
          <div className="rounded-2xl border border-gray-200 bg-white p-12 md:p-16 text-center shadow-soft">
            <div className="w-16 h-16 mx-auto rounded-2xl bg-gold-400/10 border border-gold-400/20 flex items-center justify-center">
              <svg className="w-8 h-8 text-gold-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 6.042A8.967 8.967 0 006 3.75c-1.052 0-2.062.18-3 .512v14.25A8.987 8.987 0 016 18c2.305 0 4.408.867 6 2.292m0-14.25a8.966 8.966 0 016-2.292c1.052 0 2.062.18 3 .512v14.25A8.987 8.987 0 0018 18a8.967 8.967 0 00-6 2.292m0-14.25v14.25" />
              </svg>
            </div>
            <h2 className="font-serif text-xl font-semibold text-ink-950 mt-6">No courses yet</h2>
            <p className="mt-2 text-gray-600 max-w-sm mx-auto">Check back soon for new courses.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {courses.map((c) => (
              <Link
                key={c.id}
                to={`/courses/${c.slug}`}
                className="group block rounded-2xl border border-gray-200 bg-white overflow-hidden shadow-soft hover:shadow-soft-lg hover:border-gold-400/30 transition-all duration-300"
              >
                <div className="aspect-[16/10] bg-stone flex items-center justify-center overflow-hidden relative">
                  {c.thumbnail ? (
                    <img
                      src={resolveAssetUrl(c.thumbnail)}
                      alt=""
                      className="w-full h-full object-cover group-hover:scale-[1.03] transition-transform duration-300"
                    />
                  ) : (
                    <div className="text-gray-400">
                      <svg className="w-14 h-14 mx-auto" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14" />
                      </svg>
                      <span className="text-xs mt-2 block">Course</span>
                    </div>
                  )}
                </div>
                <div className="p-6">
                  <h2 className="font-serif text-xl font-semibold text-ink-950 tracking-tight group-hover:text-gold-500 transition-colors line-clamp-2">
                    {c.title}
                  </h2>
                  <p className="mt-3 text-sm text-gray-600 line-clamp-2 leading-relaxed">
                    {c.description}
                  </p>
                  <p className="mt-3 text-sm font-semibold text-ink-900">
                    ${Number(c.price ?? 0).toFixed(2)}
                  </p>
                  <span className="mt-4 inline-flex items-center text-sm font-semibold text-gold-500 group-hover:text-gold-400 transition-colors">
                    View course
                    <svg className="w-4 h-4 ml-1" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 4.5L21 12m0 0l-7.5 7.5M21 12H3" />
                    </svg>
                  </span>
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
